import { c as _c } from "react/compiler-runtime";
import { MobileNavigationItem, MobileNavigationItemWithIcon, NavigationData } from './navigation.types';
import { ReactElement, ReactNode, useRef, useState } from 'react';
import ImageComponent from '@spa/components/image-component/image.component';
import LinkComponent from '@spa/components/link/link.component';
import { MobileNavigationMenu } from './mobile-navigation-menu.component';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import PlaceholderComponent from '@spa/components/placeholder/placeholder.component';
import classNames from 'clsx';
import styles from './mobile-navigation.component.module.scss';
import { tracker } from '@spa/lib/tracking/tracker';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
export interface CommonProps {
  readonly id: string;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly iconNavigationEntries: ReadonlyArray<MobileNavigationItemWithIcon>;
  readonly metaNavigationEntries: ReadonlyArray<MobileNavigationItem>;
  readonly quickNavigationEntries: ReadonlyArray<MobileNavigationItemWithIcon>;
}
export interface ExtendedProps extends CommonProps {
  readonly reduced: false;
  readonly navigation: NavigationData | null;
}
export interface ReducedProps extends CommonProps {
  readonly reduced: true;
}
export type MobileNavigationProps = ReducedProps | ExtendedProps;
export const MobileNavigation = props => {
  const $ = _c(45);
  const {
    id,
    isOpen,
    onClose,
    iconNavigationEntries,
    metaNavigationEntries,
    quickNavigationEntries
  } = props;
  const [wasOpen, setWasOpen] = useState(props.isOpen);
  const menuPaneRef = useRef(null);
  if (isOpen && !wasOpen) {
    setWasOpen(true);
  }
  const {
    formatMessage
  } = useNamespacedIntl("mobile-navigation");
  const t0 = !isOpen;
  let t1;
  if ($[0] !== isOpen || $[1] !== t0) {
    t1 = classNames(styles["mobile-navigation__sidebar"], {
      [styles["mobile-navigation__sidebar--open"]]: isOpen,
      [styles["mobile-navigation__sidebar--closed"]]: t0
    });
    $[0] = isOpen;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const sidebarClassNames = t1;
  const t2 = !isOpen;
  let t3;
  if ($[3] !== t2) {
    t3 = classNames(styles["mobile-navigation-overlay"], {
      [styles["mobile-navigation-overlay--closed"]]: t2
    });
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const overlayClassNames = t3;
  let t4;
  if ($[5] !== isOpen || $[6] !== onClose) {
    t4 = () => {
      isOpen && onClose();
    };
    $[5] = isOpen;
    $[6] = onClose;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const closeOverlayOnMobile = t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const menuPane = menuPaneRef.current;
      if (menuPane) {
        menuPane.scroll({
          top: 0,
          behavior: "smooth"
        });
      }
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const scrollUp = t5;
  let sidebarContent;
  if (isOpen || wasOpen) {
    if (props.reduced) {
      const t6 = `${id}-placeholder`;
      let t7;
      if ($[9] !== t6) {
        t7 = <PlaceholderComponent id={t6} ratio={1} />;
        $[9] = t6;
        $[10] = t7;
      } else {
        t7 = $[10];
      }
      sidebarContent = t7;
    } else {
      let t6;
      if ($[11] !== props.navigation?.allCategories) {
        t6 = props.navigation?.allCategories ?? [];
        $[11] = props.navigation?.allCategories;
        $[12] = t6;
      } else {
        t6 = $[12];
      }
      let t7;
      if ($[13] !== onClose || $[14] !== t6) {
        t7 = <MobileNavigationMenu items={t6} onClose={onClose} scrollUp={scrollUp} />;
        $[13] = onClose;
        $[14] = t6;
        $[15] = t7;
      } else {
        t7 = $[15];
      }
      let t8;
      if ($[16] !== iconNavigationEntries[0] || $[17] !== iconNavigationEntries.length || $[18] !== onClose) {
        t8 = iconNavigationEntries.length > 0 && <div className={styles["icon-navigation"]}>{[iconNavigationEntries[0]].map(entry => <LinkComponent link={entry.link} key={entry.text} className={styles["icon-navigation__link"]} onClick={event => {
            tracker.navigationClick({
              click_name: entry.text,
              click_position: "meta_navi",
              click_url: event.currentTarget.href
            });
            onClose();
          }}><ImageComponent src={entry.icon.src} alt="" width={24} height={24} mimeType={entry.icon.mimeType} /><span className={`${styles["icon-navigation__text"]} text-s`}>{entry.text}</span></LinkComponent>)}</div>;
        $[16] = iconNavigationEntries[0];
        $[17] = iconNavigationEntries.length;
        $[18] = onClose;
        $[19] = t8;
      } else {
        t8 = $[19];
      }
      let t9;
      if ($[20] !== onClose || $[21] !== quickNavigationEntries) {
        t9 = quickNavigationEntries.length > 0 && <ul className={styles["quick-navigation__list"]}>{quickNavigationEntries.map(entry_0 => <li key={entry_0.text}><LinkComponent link={entry_0.link} key={entry_0.text} className={styles["quick-navigation__link"]} onClick={event_0 => {
              tracker.navigationClick({
                click_name: entry_0.text,
                click_position: "meta_navi",
                click_url: event_0.currentTarget.href
              });
              onClose();
            }}><ImageComponent src={entry_0.icon.src} alt="" width={16} height={16} mimeType={entry_0.icon.mimeType} /><span className={`${styles["quick-navigation__text"]} text-s`}>{entry_0.text}</span></LinkComponent></li>)}</ul>;
        $[20] = onClose;
        $[21] = quickNavigationEntries;
        $[22] = t9;
      } else {
        t9 = $[22];
      }
      let t10;
      if ($[23] !== metaNavigationEntries || $[24] !== onClose) {
        t10 = metaNavigationEntries.length > 0 && <ul className={styles["meta-navigation"]}>{metaNavigationEntries.map(item => <li key={item.text}><LinkComponent link={item.link} key={item.text} className={styles["meta-navigation__link"]} onClick={event_1 => {
              tracker.navigationClick({
                click_name: item.text,
                click_position: "meta_navi",
                click_url: event_1.currentTarget.href
              });
              onClose();
            }}><span className={`${styles["meta-navigation__text"]} text-s`}>{item.text}</span></LinkComponent></li>)}</ul>;
        $[23] = metaNavigationEntries;
        $[24] = onClose;
        $[25] = t10;
      } else {
        t10 = $[25];
      }
      let t11;
      if ($[26] !== t10 || $[27] !== t9) {
        t11 = <div className={styles["quick-and-meta-navigation"]}>{t9}{t10}</div>;
        $[26] = t10;
        $[27] = t9;
        $[28] = t11;
      } else {
        t11 = $[28];
      }
      let t12;
      if ($[29] !== t11 || $[30] !== t7 || $[31] !== t8) {
        t12 = <>{t7}{t8}{t11}</>;
        $[29] = t11;
        $[30] = t7;
        $[31] = t8;
        $[32] = t12;
      } else {
        t12 = $[32];
      }
      sidebarContent = t12;
    }
  }
  let t6;
  if ($[33] !== formatMessage) {
    t6 = formatMessage("closeNavigation");
    $[33] = formatMessage;
    $[34] = t6;
  } else {
    t6 = $[34];
  }
  let t7;
  if ($[35] !== closeOverlayOnMobile || $[36] !== overlayClassNames || $[37] !== t6) {
    t7 = <button type="button" className={overlayClassNames} onClick={closeOverlayOnMobile} onKeyDown={closeOverlayOnMobile} onFocus={closeOverlayOnMobile} aria-label={t6} />;
    $[35] = closeOverlayOnMobile;
    $[36] = overlayClassNames;
    $[37] = t6;
    $[38] = t7;
  } else {
    t7 = $[38];
  }
  let t8;
  if ($[39] !== sidebarClassNames || $[40] !== sidebarContent) {
    t8 = <div ref={menuPaneRef} className={sidebarClassNames} data-testid="sidebar">{sidebarContent}</div>;
    $[39] = sidebarClassNames;
    $[40] = sidebarContent;
    $[41] = t8;
  } else {
    t8 = $[41];
  }
  let t9;
  if ($[42] !== t7 || $[43] !== t8) {
    t9 = <>{t7}{t8}</>;
    $[42] = t7;
    $[43] = t8;
    $[44] = t9;
  } else {
    t9 = $[44];
  }
  return t9;
};